// Code splitting that works with prerendering and hydration.
//  - The prerenderer calls preloadAll() first, so every lazy component renders synchronously into the static HTML.
//  - In the browser, a lazy component that is not loaded yet suspends. Inside a <Suspense> boundary during hydration,
//    React keeps the server-rendered HTML on screen and hydrates that part once the chunk arrives (selective
//    hydration), so nothing flashes or disappears.
//  - `idle: true` defers the download until the browser is idle; otherwise it starts at once (e.g. the current page).
import { Suspense, type ComponentType, type ReactNode } from 'react'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Loader = () => Promise<{ default: ComponentType<any> }>
export interface LazyComponent<P> { (props: P): ReactNode; preload: () => Promise<void> }

const all: (() => Promise<void>)[] = []
const onIdle = () => new Promise<void>(resolve => {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback
  if (ric) ric(() => resolve(), { timeout: 2000 }); else setTimeout(resolve, 200)
})

export function lazyComponent<P extends object = object>(load: Loader, { idle = false } = {}): LazyComponent<P> {
  let Comp: ComponentType<P> | null = null
  let pending: Promise<void> | null = null
  const preload = () => (pending ??= load().then(m => { Comp = m.default }))
  const Lazy = ((props: P) => {
    if (Comp) return <Comp {...props} />
    throw idle ? (pending ??= onIdle().then(() => load()).then(m => { Comp = m.default })) : preload()
  }) as LazyComponent<P>
  Lazy.preload = preload
  all.push(preload)
  return Lazy
}

/** Prerendering: load every lazy component so renderToString never suspends. Loading a page chunk can register
 *  more lazy components (e.g. the calculator inside /when), so repeat until no new ones appear. */
export async function preloadAll() {
  for (let done = 0; done < all.length;) { const batch = all.slice(done); done = all.length; await Promise.all(batch.map(p => p())) }
}

/** A Suspense boundary that renders nothing while loading (hydration keeps the server HTML instead). */
export const Deferred = ({ children }: { children: ReactNode }) => <Suspense fallback={null}>{children}</Suspense>
